/**
 * Freshness-chip formatting — sync module.
 *
 * Two clocks meet in this file and confusing them is the whole hazard:
 *
 *  - `dataAsOf` is a NAIVE Turkish wall-clock string (`YYYY-MM-DD HH:mm:ss.SSS`).
 *    It is never parsed. `new Date(dataAsOf)` would read it as the browser's
 *    local time and silently move the label by the visitor's offset, so both
 *    the rendering and the staleness comparison here are pure string math
 *    (repo timestamp rule — see CLAUDE.md).
 *  - `syncedAt` is a real instant (ISO UTC). "N dk önce" is a distance between
 *    two instants, so Date math is the correct tool there and only there.
 *
 * Helpers are duplicated per feature by house convention (see finans/format.ts)
 * rather than shared, so one screen's rounding never drags another's with it.
 */

/** Beyond this the chip warns: a day-old mirror is a fact the reader must see. */
export const STALE_AFTER_HOURS = 24

const HOUR_MS = 60 * 60 * 1000

/**
 * Turkish wall clock of a real instant, as the sortable `YYYY-MM-DD HH:mm`.
 *
 * `hourCycle: 'h23'` is deliberate: the h24 cycle renders midnight as `24:00`
 * on the PREVIOUS day, which would compare wrong against a naive `00:xx`.
 * Assembled from parts rather than `format()` so no locale's separator or
 * digit-shaping can reach the comparison key.
 */
const istanbulClock = new Intl.DateTimeFormat('sv-SE', {
  timeZone: 'Europe/Istanbul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

export function istanbulWallClock(instant: Date): string {
  const parts = istanbulClock.formatToParts(instant)
  const part = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((candidate) => candidate.type === type)?.value ?? ''
  return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}`
}

/**
 * `YYYY-MM-DD HH:mm:ss.SSS` → the same minute as a comparison key, or null when
 * the string is not that shape. Accepts an ISO-style `T` separator too, so a
 * source that ever tightens its serialization cannot quietly break the compare.
 */
function wallClockKey(dataAsOf: string): string | null {
  const date = dataAsOf.slice(0, 10)
  const time = dataAsOf.slice(11, 16)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null
  return `${date} ${time}`
}

/** `2026-08-11 18:57:03.120` → `11.08 18:57`. The year is left to the tooltip. */
export function formatDataAsOf(dataAsOf: string): string | null {
  const key = wallClockKey(dataAsOf)
  if (!key) return null
  return `${key.slice(8, 10)}.${key.slice(5, 7)} ${key.slice(11, 16)}`
}

/** `2026-08-11 18:57:03.120` → `11.08.2026 18:57` — the tooltip's full form. */
export function formatDataAsOfLong(dataAsOf: string): string | null {
  const key = wallClockKey(dataAsOf)
  if (!key) return null
  return `${key.slice(8, 10)}.${key.slice(5, 7)}.${key.slice(0, 4)} ${key.slice(11, 16)}`
}

/**
 * Is the data horizon more than a day behind Turkish now?
 *
 * Both sides are `YYYY-MM-DD HH:mm` strings, so a plain string comparison is the
 * date comparison — that is the point of the sortable format. The cutoff is a
 * real instant minus 24h, formatted INTO Istanbul; Turkey has no DST, so that
 * is exactly the wall clock of a day ago.
 */
export function isStale(dataAsOf: string, now: Date = new Date()): boolean {
  const key = wallClockKey(dataAsOf)
  if (!key) return false
  return key < istanbulWallClock(new Date(now.getTime() - STALE_AFTER_HOURS * HOUR_MS))
}

/**
 * `syncedAt` (ISO UTC instant) → `12 dk önce`. Null when unparseable.
 *
 * A future stamp means the two clocks disagree, not that the sync is ahead of
 * us; it reads as "az önce" rather than a negative number.
 */
export function formatRelativeToNow(syncedAt: string, now: Date = new Date()): string | null {
  const instant = Date.parse(syncedAt)
  if (Number.isNaN(instant)) return null

  const minutes = Math.floor((now.getTime() - instant) / 60_000)
  if (minutes < 1) return 'az önce'
  if (minutes < 60) return `${minutes} dk önce`

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours} saat önce`

  return `${Math.floor(hours / 24)} gün önce`
}

const countFormat = new Intl.NumberFormat('tr-TR')

export function formatCount(value: number): string {
  return countFormat.format(value)
}
